import {ReactNode, useContext, useEffect, useState} from "react";
import Page from "../../api/Page";
import Folder from "../../api/folder/Folder";
import GetListFolderQuery from "../../api/folder/GetListFolderQuery";
import FolderContext from "./FolderContext";
import FolderApi from "../../api/folder/FolderApi";
import Notifications from "../../utils/notifications/Notifications";
import AuthContext from "../auth/AuthContext";
import useFetching from "../../hooks/useFetching";
import CreateFolderPayload from "../../api/folder/CreateFolderPayload";

type WrapFolderContextProps = {
    /**
     * Children to be wrapped inside a provider.
     * @author Kacper Faber
     * @since 0.1.7
     */
    children: ReactNode | ReactNode[] | null;
};

function WrapFolderContext(props: WrapFolderContextProps) {
    /**
     * Current authenticated user in auth context.
     * @author Kacper Faber
     * @since 0.1.7
     */
    const {user} = useContext(AuthContext);

    /**
     * Query to fetch folders.
     * @author Kacper Faber
     * @since 0.1.7
     */
    const [query, setQuery] = useState<GetListFolderQuery>({});

    /**
     * Folders data.
     * Fetch at reload method.
     * @author Kacper Faber
     * @since 0.1.7
     */
    const [folders, setFolders] = useState<Page<Folder>>();

    /**
     * Is fetching.
     * @author Kacper Faber
     * @since 0.1.7
     */
    const [isFetching, startFetching, stopFetching] = useFetching(false);

    /**
     * Implementation of 'deleteFolder' method in FolderContext.
     * Deletes folder and reloads the state.
     * @param id
     */
    const deleteFolder = (id: string | number) =>
        FolderApi.deleteById(id).then(reload);

    /**
     * Create folder and refresh the state
     * @author Kacper Faber
     * @since 0.1.8
     * @param payload
     */
    const create = async (payload: CreateFolderPayload) => {
        const folder = await FolderApi.create(payload);
        reload();
        return folder;
    }

    /**
     * Reload folders in context [in 'folders' state].
     * Does nothing if AuthContext.User is undefined.
     * @author Kacper Faber
     * @since 0.1.7
     */
    const reload = () => {
        if (!user) {
            return;
        }

        startFetching();

        FolderApi.getList(query)
            .then(setFolders)
            .catch(Notifications.onFetchError)
            .finally(stopFetching);
    }

    useEffect(reload, [query, user]);

    return (
        <FolderContext.Provider
            value={{ folders, query, setQuery, isFetching, reload, deleteFolder, create }}
            children={props.children}
        />
    );
}

// noinspection JSUnusedGlobalSymbols
export default WrapFolderContext;